import { decodeBase64Url, encodeBase64Url } from "@/utils/str";
import { deepCopy, deepMerge, deepSet } from "@/utils";
import { toTitleCase } from "@/utils/str";
import mitt from "mitt";
import { onBeforeMount, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

export const filters = (...filter) => {
    return {
        get all() {
            return filter;
        },
    }
}

export const checkboxFilter = ({
    name,
    label,
    props
}) => {
    return filter({
        name, label, props, type: 'checkbox'
    });
}

export const selectFilter = ({
    name,
    label,
    props
}) => {
    return filter({
        name, label, props, type: 'select'
    });
}

export const switchFilter = ({
    name,
    label,
    props
}) => {
    return filter({
        name, label, props, type: 'switch'
    });
}

export const filter = ({
    name,
    label = null,
    type = null,
    props = null,
}) => {
    return {
        get name() {
            return name;
        },
        get label() {
            return label ? label : toTitleCase(name);
        },
        get props() {
            return props;
        },
        componentName() {
            const components = {
                checkbox: 'app-form-checkbox',
                switch: 'app-form-switch',
                select: 'app-form-select',
            }

            return components[type];
        },
    }
};

export const useCollectionView = ({ props, el }) => {
    const event = mitt();
    const route = useRoute();
    const router = useRouter();
    const activeViewKey = ref(null);
    const viewQuery = reactive({});
    const viewStates = reactive({});

    const mergeFilters = (view) => {
        const filters = view.collection.getParams().filters ?? {};

        return filters;
    }

    const getViewParams = (view) => {
        return {
            ...view.collection.getParams(),
            filters: mergeFilters(view),
        }
    }

    const renderer = {
        get views() {
            return props.views.map((view) => {
                return {
                    get query() {
                        return viewQuery[view.key];
                    },
                    get search() {
                        if (!viewQuery[view.key]) return null;

                        return viewQuery[view.key].q ?? null;
                    },
                    get filters() {
                        if (!viewQuery[view.key]) return null;

                        return viewQuery[view.key].f ?? null;
                    },
                    get key() {
                        return view.key;
                    },
                    get label() {
                        return view.label;
                    },
                    get data() {
                        return view.collection.data;
                    },
                    get collection() {
                        return view.collection;
                    },
                    get queryViewOpen() {
                        return viewStates[view.key]?.queryViewOpen;
                    },
                    _setState(key, value) {
                        if (!viewStates[view.key]) {
                            viewStates[view.key] = {};
                        }

                        deepSet(viewStates[view.key], key, value);
                    },
                    showQueryView() {
                        this._setState('queryViewOpen', true);
                    },
                    hideQueryView() {
                        this._setState('queryViewOpen', false);
                    },
                    toggleQueryView() {
                        if (this.queryViewOpen) {
                            this.hideQueryView();
                        } else {
                            this.showQueryView();
                        }
                    },
                    hasQuery() {
                        return this.query && Object.keys(this.query).length > 0;
                    },
                    setQuery(value, key = null) {
                        if (key) {
                            const query = deepCopy(viewQuery[view.key] ?? {});
                            deepSet(query, key, value);
                            viewQuery[view.key] = query;
                        } else {
                            viewQuery[view.key] = value;
                        }

                        if (props.viewInQuery) {
                            router.push({
                                replace: true,
                                query: {
                                    ...route.query,
                                    [props.queryString]: this.hasQuery()
                                        ? encodeBase64Url(JSON.stringify(this.query))
                                        : undefined,
                                }
                            });
                        }

                        return;
                    },
                    get filters () {
                        return props.filters.all;
                    },
                    setSearch(str) {
                        this.setQuery(str ? str : undefined, 'q');
                    },
                    clearSearch() {
                        this.setQuery(undefined, 'q');
                    },
                    clearFilter() {
                        this.setQuery(undefined, 'f');
                    },
                    setFilters(obj) {
                        this.setQuery(obj, 'f');
                    },
                    queryParams() {
                        if (this.query) {
                            return {};
                        }
                    },
                    buildFetchParams() {
                        const defaultParams = deepCopy(this.collection.getDefaultParams());
                        if (!this.hasQuery()) {
                            return defaultParams;
                        }

                        const params = {
                            query: this.query.q,
                            filters: Object.keys(this.query.f ?? []).reduce((acc, filterKey) => {
                                acc[filterKey] = this.query.f[filterKey];

                                return acc;
                            }, {}),
                        }

                        return deepMerge(defaultParams, params);
                    },
                    async fetch() {
                        return this.collection.fetch({
                            params: this.buildFetchParams(),
                        });
                    },
                    async fetchOnce() {
                        return this.collection.fetchOnce({
                            params: this.buildFetchParams(),
                        });
                    },
                };
            })
        },
        get props() {
            return props;
        },
        get event() {
            return event;
        },
        get el() {
            return el;
        },
        get activeView() {
            if (!activeViewKey.value) return null;

            return this.views.find(view => view.key === activeViewKey.value);
        },
        async renderView(key) {
            activeViewKey.value = key;

            if (props.viewInQuery) {
                router.push({
                    replace: true,
                    query: {
                        ...route.query,
                        [props.viewQueryString]: key,
                        [props.queryString]: this.activeView.hasQuery()
                            ? encodeBase64Url(JSON.stringify(this.activeView.query))
                            : undefined,
                    }
                });
            }

            return this.activeView.fetch();
        },
        configure() {
            if (props.viewInQuery && !props.viewQueryString) {
                throw new Error('viewQueryString is required when using viewInQuery');
            }

            if (props.viewInQuery && !props.queryString) {
                throw new Error('queryString is required when using viewInQuery');
            }

            if (props.defaultView && !props.views.find(view => view.key === props.defaultView)) {
                throw new Error(`Default view not found: ${props.defaultView}`);
            }

            if (props.searchInQuery && !props.searchQueryString) {
                throw new Error('searchQueryString is required when using searchInQuery');
            }

            let view;
            if (props.defaultView) {
                view = props.views.find(view => view.key === props.defaultView);
            } else {
                view = props.views[0];
            }

            if (props.viewInQuery && route.query[props.viewQueryString]) {
                view = props.views.find(view => view.key === route.query[props.viewQueryString]);
            }

            activeViewKey.value = view.key;

            if (props.viewInQuery && route.query[props.queryString]) {
                try {
                    this.activeView.setQuery(JSON.parse(decodeBase64Url(route.query[props.queryString])));
                } catch (error) {
                    console.error(error);
                    this.activeView.setQuery(null);
                }
            } else {
                this.activeView.setQuery(null);
            }
        },
    };

    onBeforeMount(() => renderer.configure());
    onMounted(() => props.fetchOnce ? renderer.activeView.fetchOnce() : renderer.activeView.fetch());

    return renderer;
}
